/**
 * Tier Enforcement Configuration UI
 * Manage tier limits for FREE and PRO subscriptions
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';

interface TierLimits {
  ai: {
    daily_requests: number;
    allowed_models: string[];
    max_cost_per_request: number;
    max_tokens_per_request: number;
    priority_access: boolean;
  };
  storage: {
    max_documents: number;
    max_storage_mb: number;
    allowed_formats: string[];
  };
  character: {
    max_character_slots: number;
    pro_skins_enabled: boolean;
    customization_options: string[];
  };
  export: {
    allowed_formats: string[];
    max_exports_per_day: number;
  };
  analytics: {
    advanced_analytics: boolean;
    custom_reports: boolean;
    data_retention_days: number;
  };
  support: {
    priority_support: boolean;
    max_response_time_hours: number;
  };
  platform: {
    web_access: boolean;
    mobile_access: boolean;
    desktop_access: boolean;
    api_access: boolean;
  };
  game_generation: {
    daily_requests: number;
    concurrent_requests: number;
    max_history_days: number;
  };
  tts: {
    daily_requests: number;
    allowed_models: string[];
    pro_voices_allowed: boolean;
    max_characters_per_request: number;
  };
}

interface TierConfig {
  free: TierLimits;
  pro: TierLimits;
}

export class TierEnforcementUI extends BaseComponent {
  private apiClient: APIClient;
  private tiers: TierConfig | null = null;
  private allModels: string[] = [];
  private editingTier: 'free' | 'pro' | null = null;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="tier-enforcement-ui">
        <div class="tier-header">
          <h2>🎯 Tier Enforcement Configuration</h2>
          <p class="subtitle">Manage subscription tier limits and permissions</p>
        </div>

        <div class="tier-toolbar">
          <button id="refresh-btn" class="btn btn-secondary">
            <span>🔄</span> Refresh
          </button>
        </div>

        <div id="tiers-container" class="tiers-container">
          <div class="loading">Loading tier configuration...</div>
        </div>

        <div id="tier-edit-modal" class="modal" style="display: none;">
          <div class="modal-content large-modal">
            <div class="modal-header">
              <h3 id="modal-title">Edit Tier Limits</h3>
              <button id="modal-close" class="close-btn">&times;</button>
            </div>
            <div id="modal-body" class="modal-body">
              <!-- Form will be injected -->
            </div>
          </div>
        </div>
      </div>
    `;

    document.getElementById('refresh-btn')?.addEventListener('click', () => this.loadTiers());
    document.getElementById('modal-close')?.addEventListener('click', () => this.hideModal());
    await this.loadModels();
    await this.loadTiers();
  }

  private async loadModels(): Promise<void> {
    try {
      const response = await this.apiClient.get<{ models: Record<string, any> }>('/v1/admin/config/models');
      this.allModels = Object.keys(response.data.models || {});
    } catch (error) {
      console.error('Failed to load models:', error);
      this.allModels = [];
    }
  }

  private async loadTiers(): Promise<void> {
    const container = document.getElementById('tiers-container');
    if (!container) return;

    container.innerHTML = '<div class="loading">⏳ Loading...</div>';

    try {
      const response = await this.apiClient.get<{ tiers: TierConfig; source: string }>('/v1/admin/config/tier-enforcement');
      this.tiers = response.data.tiers;
      this.renderTiers();
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to load tier configuration</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    }
  }

  private renderTiers(): void {
    const container = document.getElementById('tiers-container');
    if (!container || !this.tiers) return;

    container.innerHTML = `
      <div class="tiers-grid">
        ${this.renderTierCard('free', this.tiers.free)}
        ${this.renderTierCard('pro', this.tiers.pro)}
      </div>
    `;

    // Attach event listeners
    document.getElementById('edit-free')?.addEventListener('click', () => this.showEditModal('free'));
    document.getElementById('edit-pro')?.addEventListener('click', () => this.showEditModal('pro'));
    document.getElementById('reset-free')?.addEventListener('click', () => this.resetTier('free'));
    document.getElementById('reset-pro')?.addEventListener('click', () => this.resetTier('pro'));
  }

  private renderTierCard(tier: 'free' | 'pro', limits: TierLimits): string {
    const tierName = tier === 'free' ? 'FREE' : 'PRO';
    const tierColor = tier === 'free' ? '#64748b' : '#3b82f6';

    return `
      <div class="tier-card" style="border-left: 4px solid ${tierColor}">
        <div class="tier-card-header">
          <div>
            <h3 style="color: ${tierColor}">${tierName} Tier</h3>
            <p class="tier-subtitle">${tier === 'free' ? 'Basic features for all users' : 'Pro features for subscribers'}</p>
          </div>
          <div class="tier-actions">
            <button id="edit-${tier}" class="icon-btn" title="Edit">✏️</button>
            <button id="reset-${tier}" class="icon-btn" title="Reset to default">🔄</button>
          </div>
        </div>

        <div class="tier-sections">
          <div class="tier-section">
            <h4>🤖 AI Features</h4>
            <ul>
              <li><strong>Daily Requests:</strong> ${limits.ai?.daily_requests?.toLocaleString() ?? '0'}</li>
              <li><strong>Max Cost/Request:</strong> $${limits.ai?.max_cost_per_request ?? '0'}</li>
              <li><strong>Max Tokens/Request:</strong> ${limits.ai?.max_tokens_per_request?.toLocaleString() ?? '0'}</li>
              <li><strong>Priority Access:</strong> ${limits.ai?.priority_access ? '✅' : '❌'}</li>
              <li><strong>Allowed Models (${limits.ai?.allowed_models?.length ?? 0}):</strong>
                <div class="model-chips">
                  ${(limits.ai?.allowed_models || []).slice(0, 3).map(m => `<code class="chip">${m}</code>`).join('')}
                  ${(limits.ai?.allowed_models || []).length > 3 ? `<span class="more-badge">+${limits.ai.allowed_models.length - 3}</span>` : ''}
                </div>
              </li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>💾 Storage</h4>
            <ul>
              <li><strong>Max Documents:</strong> ${limits.storage?.max_documents?.toLocaleString() ?? '0'}</li>
              <li><strong>Max Storage:</strong> ${limits.storage?.max_storage_mb ?? '0'}MB</li>
              <li><strong>Formats:</strong> ${limits.storage?.allowed_formats?.join(', ') ?? 'None'}</li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>👤 Character</h4>
            <ul>
              <li><strong>Max Slots:</strong> ${limits.character?.max_character_slots ?? '0'}</li>
              <li><strong>Pro Skins:</strong> ${limits.character?.pro_skins_enabled ? '✅' : '❌'}</li>
              <li><strong>Customization:</strong> ${limits.character?.customization_options?.join(', ') ?? 'None'}</li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>📤 Export & Analytics</h4>
            <ul>
              <li><strong>Export Formats:</strong> ${limits.export?.allowed_formats?.join(', ') ?? 'None'}</li>
              <li><strong>Max Exports/Day:</strong> ${limits.export?.max_exports_per_day ?? '0'}</li>
              <li><strong>Advanced Analytics:</strong> ${limits.analytics?.advanced_analytics ? '✅' : '❌'}</li>
              <li><strong>Data Retention:</strong> ${limits.analytics?.data_retention_days ?? '0'} days</li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>🛠️ Support & Platform</h4>
            <ul>
              <li><strong>Priority Support:</strong> ${limits.support?.priority_support ? '✅' : '❌'}</li>
              <li><strong>Max Response Time:</strong> ${limits.support?.max_response_time_hours ?? '0'}h</li>
              <li><strong>Web Access:</strong> ${limits.platform?.web_access ? '✅' : '❌'}</li>
              <li><strong>Mobile Access:</strong> ${limits.platform?.mobile_access ? '✅' : '❌'}</li>
              <li><strong>Desktop Access:</strong> ${limits.platform?.desktop_access ? '✅' : '❌'}</li>
              <li><strong>API Access:</strong> ${limits.platform?.api_access ? '✅' : '❌'}</li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>🎮 Game Generation</h4>
            <ul>
              <li><strong>Daily Requests:</strong> ${limits.game_generation?.daily_requests?.toLocaleString() ?? '0'}</li>
              <li><strong>Concurrent:</strong> ${limits.game_generation?.concurrent_requests ?? '0'}</li>
              <li><strong>History:</strong> ${limits.game_generation?.max_history_days ?? '0'} days</li>
            </ul>
          </div>

          <div class="tier-section">
            <h4>🎙️ TTS (Speech)</h4>
            <ul>
              <li><strong>Daily Requests:</strong> ${limits.tts?.daily_requests?.toLocaleString() ?? '0'}</li>
              <li><strong>Pro Voices:</strong> ${limits.tts?.pro_voices_allowed ? '✅' : '❌'}</li>
              <li><strong>Max Chars/Req:</strong> ${limits.tts?.max_characters_per_request?.toLocaleString() ?? '0'}</li>
              <li><strong>Allowed Models (${limits.tts?.allowed_models?.length ?? 0}):</strong>
                <div class="model-chips">
                  ${(limits.tts?.allowed_models || []).slice(0, 3).map(m => `<code class="chip">${m}</code>`).join('')}
                  ${(limits.tts?.allowed_models || []).length > 3 ? `<span class="more-badge">+${limits.tts.allowed_models.length - 3}</span>` : ''}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  private showEditModal(tier: 'free' | 'pro'): void {
    this.editingTier = tier;
    if (!this.tiers) return;

    const limits = tier === 'free' ? this.tiers.free : this.tiers.pro;
    const modal = document.getElementById('tier-edit-modal');
    const modalTitle = document.getElementById('modal-title');
    const modalBody = document.getElementById('modal-body');

    if (!modal || !modalTitle || !modalBody) return;

    modalTitle.textContent = `Edit ${tier.toUpperCase()} Tier Limits`;

    // Build form HTML
    modalBody.innerHTML = `
      <form id="tier-form" class="tier-form">
        <div class="form-columns">
          <!-- AI Section -->
          <div class="form-section">
            <h4>🤖 AI Features</h4>
            <div class="form-group">
              <label>Daily Requests</label>
              <input type="number" id="ai_daily_requests" value="${limits.ai?.daily_requests ?? 0}" min="1" />
            </div>
            <div class="form-group">
              <label>Max Cost per Request ($)</label>
              <input type="number" id="ai_max_cost" value="${limits.ai?.max_cost_per_request ?? 0}" step="0.001" min="0" />
            </div>
            <div class="form-group">
              <label>Max Tokens per Request</label>
              <input type="number" id="ai_max_tokens" value="${limits.ai?.max_tokens_per_request ?? 0}" min="1" />
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="ai_priority" ${limits.ai?.priority_access ? 'checked' : ''} /> Priority Access</label>
            </div>
            <div class="form-group">
              <label>Allowed Models</label>
              <div class="model-selector">
                ${this.allModels.map(m => `
                  <label class="model-checkbox">
                    <input type="checkbox" name="ai_models" value="${m}" ${(limits.ai?.allowed_models || []).includes(m) ? 'checked' : ''} />
                    <code>${m}</code>
                  </label>
                `).join('')}
              </div>
            </div>
          </div>

          <!-- Storage Section -->
          <div class="form-section">
            <h4>💾 Storage</h4>
            <div class="form-group">
              <label>Max Documents</label>
              <input type="number" id="storage_max_docs" value="${limits.storage?.max_documents ?? 10}" min="1" />
            </div>
            <div class="form-group">
              <label>Max Storage (MB)</label>
              <input type="number" id="storage_max_mb" value="${limits.storage?.max_storage_mb ?? 100}" min="1" />
            </div>
            <div class="form-group">
              <label>Allowed Formats (comma-separated)</label>
              <input type="text" id="storage_formats" value="${limits.storage?.allowed_formats?.join(', ') ?? 'pdf, txt'}" />
            </div>
          </div>

          <!-- Character Section -->
          <div class="form-section">
            <h4>👤 Character</h4>
            <div class="form-group">
              <label>Max Character Slots</label>
              <input type="number" id="character_slots" value="${limits.character?.max_character_slots ?? 1}" min="1" />
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="character_skins" ${limits.character?.pro_skins_enabled ? 'checked' : ''} /> Pro Skins Enabled</label>
            </div>
            <div class="form-group">
              <label>Customization Options (comma-separated)</label>
              <input type="text" id="character_options" value="${limits.character?.customization_options?.join(', ') ?? 'basic'}" />
            </div>
          </div>

          <!-- Export & Analytics Section -->
          <div class="form-section">
            <h4>📤 Export & Analytics</h4>
            <div class="form-group">
              <label>Export Formats (comma-separated)</label>
              <input type="text" id="export_formats" value="${limits.export?.allowed_formats?.join(', ') ?? 'json'}" />
            </div>
            <div class="form-group">
              <label>Max Exports per Day</label>
              <input type="number" id="export_max" value="${limits.export?.max_exports_per_day ?? 1}" min="1" />
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="analytics_advanced" ${limits.analytics?.advanced_analytics ? 'checked' : ''} /> Advanced Analytics</label>
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="analytics_reports" ${limits.analytics?.custom_reports ? 'checked' : ''} /> Custom Reports</label>
            </div>
            <div class="form-group">
              <label>Data Retention (days)</label>
              <input type="number" id="analytics_retention" value="${limits.analytics?.data_retention_days ?? 7}" min="1" />
            </div>
          </div>

          <!-- Support & Platform Section -->
          <div class="form-section">
            <h4>🛠️ Support & Platform</h4>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="support_priority" ${limits.support?.priority_support ? 'checked' : ''} /> Priority Support</label>
            </div>
            <div class="form-group">
              <label>Max Response Time (hours)</label>
              <input type="number" id="support_response" value="${limits.support?.max_response_time_hours ?? 24}" min="1" />
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="platform_web" ${limits.platform?.web_access ? 'checked' : ''} /> Web Access</label>
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="platform_mobile" ${limits.platform?.mobile_access ? 'checked' : ''} /> Mobile Access</label>
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="platform_desktop" ${limits.platform?.desktop_access ? 'checked' : ''} /> Desktop Access</label>
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="platform_api" ${limits.platform?.api_access ? 'checked' : ''} /> API Access</label>
            </div>
          </div>

          <!-- Game Generation Section -->
          <div class="form-section">
            <h4>🎮 Game Generation</h4>
            <div class="form-group">
              <label>Daily Requests</label>
              <input type="number" id="gamgen_daily_requests" value="${limits.game_generation?.daily_requests ?? 0}" min="0" />
            </div>
            <div class="form-group">
              <label>Concurrent Requests</label>
              <input type="number" id="gamgen_concurrent" value="${limits.game_generation?.concurrent_requests ?? 1}" min="1" />
            </div>
            <div class="form-group">
              <label>Max History (days)</label>
              <input type="number" id="gamgen_history" value="${limits.game_generation?.max_history_days ?? 7}" min="1" />
            </div>
          </div>

          <!-- TTS Section -->
          <div class="form-section">
            <h4>🎙️ TTS (Speech)</h4>
            <div class="form-group">
              <label>Daily Requests</label>
              <input type="number" id="tts_daily_requests" value="${limits.tts?.daily_requests ?? 0}" min="0" />
            </div>
            <div class="form-group checkbox">
              <label><input type="checkbox" id="tts_pro_voices" ${limits.tts?.pro_voices_allowed ? 'checked' : ''} /> Pro Voices Allowed</label>
            </div>
            <div class="form-group">
              <label>Max Characters per Request</label>
              <input type="number" id="tts_max_chars" value="${limits.tts?.max_characters_per_request ?? 0}" min="0" />
            </div>
            <div class="form-group">
              <label>Allowed Models</label>
              <div class="model-selector">
                ${this.getTtsModelOptions().map(m => `
                  <label class="model-checkbox">
                    <input type="checkbox" name="tts_models" value="${m}" ${(limits.tts?.allowed_models || []).includes(m) ? 'checked' : ''} />
                    <code>${m}</code>
                  </label>
                `).join('')}
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="button" id="cancel-btn" class="btn btn-secondary">Cancel</button>
          <button type="submit" class="btn btn-primary">Save Changes</button>
        </div>
      </form>
    `;

    // Setup form submit
    const form = document.getElementById('tier-form') as HTMLFormElement;
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.saveTier();
    });

    document.getElementById('cancel-btn')?.addEventListener('click', () => this.hideModal());

    modal.style.display = 'flex';
  }

  private async saveTier(): Promise<void> {
    if (!this.editingTier) return;

    // Collect form data
    const getInput = (id: string) => (document.getElementById(id) as HTMLInputElement)?.value;
    const getChecked = (id: string) => (document.getElementById(id) as HTMLInputElement)?.checked;
    const getModels = () => Array.from(document.querySelectorAll('input[name="ai_models"]:checked'))
      .map(el => (el as HTMLInputElement).value);
    const getTtsModels = () => Array.from(document.querySelectorAll('input[name="tts_models"]:checked'))
      .map(el => (el as HTMLInputElement).value);

    const limits: TierLimits = {
      ai: {
        daily_requests: parseInt(getInput('ai_daily_requests') || '0'),
        allowed_models: getModels(),
        max_cost_per_request: parseFloat(getInput('ai_max_cost') || '0'),
        max_tokens_per_request: parseInt(getInput('ai_max_tokens') || '0'),
        priority_access: getChecked('ai_priority') || false,
      },
      storage: {
        max_documents: parseInt(getInput('storage_max_docs') || '0'),
        max_storage_mb: parseInt(getInput('storage_max_mb') || '0'),
        allowed_formats: (getInput('storage_formats') || '').split(',').map(s => s.trim()).filter(Boolean),
      },
      character: {
        max_character_slots: parseInt(getInput('character_slots') || '0'),
        pro_skins_enabled: getChecked('character_skins') || false,
        customization_options: (getInput('character_options') || '').split(',').map(s => s.trim()).filter(Boolean),
      },
      export: {
        allowed_formats: (getInput('export_formats') || '').split(',').map(s => s.trim()).filter(Boolean),
        max_exports_per_day: parseInt(getInput('export_max') || '0'),
      },
      analytics: {
        advanced_analytics: getChecked('analytics_advanced') || false,
        custom_reports: getChecked('analytics_reports') || false,
        data_retention_days: parseInt(getInput('analytics_retention') || '0'),
      },
      support: {
        priority_support: getChecked('support_priority') || false,
        max_response_time_hours: parseInt(getInput('support_response') || '0'),
      },
      platform: {
        web_access: getChecked('platform_web') || false,
        mobile_access: getChecked('platform_mobile') || false,
        desktop_access: getChecked('platform_desktop') || false,
        api_access: getChecked('platform_api') || false,
      },
      game_generation: {
        daily_requests: parseInt(getInput('gamgen_daily_requests') || '0'),
        concurrent_requests: parseInt(getInput('gamgen_concurrent') || '0'),
        max_history_days: parseInt(getInput('gamgen_history') || '0'),
      },
      tts: {
        daily_requests: parseInt(getInput('tts_daily_requests') || '0'),
        pro_voices_allowed: getChecked('tts_pro_voices'),
        max_characters_per_request: parseInt(getInput('tts_max_chars') || '0'),
        allowed_models: getTtsModels(),
      }
    };

    try {
      await this.apiClient.put('/v1/admin/config/tier-enforcement', {
        tier: this.editingTier,
        limits,
      });

      alert('✅ Tier limits updated successfully!');
      this.hideModal();
      await this.loadTiers();
    } catch (error) {
      alert(`❌ Failed to save: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  private async resetTier(tier: 'free' | 'pro'): Promise<void> {
    if (!confirm(`Reset ${tier.toUpperCase()} tier to default configuration?\\n\\nThis will remove the KV override.`)) {
      return;
    }

    try {
      await this.apiClient.delete(`/v1/admin/config/tier-enforcement/${tier}`);
      alert('✅ Tier reset to default!');
      await this.loadTiers();
    } catch (error) {
      alert(`❌ Failed: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  private hideModal(): void {
    const modal = document.getElementById('tier-edit-modal');
    if (modal) modal.style.display = 'none';
    this.editingTier = null;
  }

  destroy(): void {
    this.container.innerHTML = '';
  }

  private getTtsModelOptions(): string[] {
    const ttsModels = this.allModels.filter(m =>
      m.startsWith('azure-tts/') ||
      m.startsWith('elevenlabs/') ||
      m.startsWith('openai-tts/') ||
      m.startsWith('deepgram-tts/') ||
      m.startsWith('cartesia/')
    );

    if (ttsModels.length > 0) return ttsModels;

    return [
      'azure-tts/neural',
      'elevenlabs/eleven_multilingual_v2',
      'elevenlabs/eleven_flash_v2_5'
    ];
  }
}
